import { AzureCommunicationTokenCredential } from "@azure/communication-common";
import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { logger as globalLogger } from "~/utils/logger-v2";

const logger = globalLogger.namespace("[UserStore]");

interface User {
	username: string;
	id: string;
}

interface SelfACSUser {
	id: string;
	token: string;
	credential: AzureCommunicationTokenCredential;
}

export type SelfUser = Partial<User & { communicationId: string }>;

interface UserStore {
	user: User;
	acs: Partial<SelfACSUser>;
	getUser: () => SelfUser;
	setUser: (user: Partial<User>) => void;
	setUsername: (username: string) => void;
	setACSTokenIdentity: (acsUser: Omit<SelfACSUser, "credential">) => void;
}

const useUserStore = create(
	persist<UserStore>(
		(set, get) => ({
			user: {
				username: "",
				id: uuidv4(),
			},
			acs: {
				identity: undefined,
				token: undefined,
				credential: undefined,
			},
			getUser: () => {
				const { user, acs } = get();

				return { ...user, communicationId: acs.id };
			},
			setUsername: (username) =>
				set(({ user }) => ({ user: { ...user, username } })),
			setUser: (user) =>
				set(({ user: oldUser }) => ({ user: { ...oldUser, ...user } })),
			setACSTokenIdentity: ({ token, id }) => {
				try {
					const credential = new AzureCommunicationTokenCredential(token);
					set(() => ({
						acs: {
							id,
							token,
							credential,
						},
					}));
					logger.info("Successfully created ACS credential");
				} catch (error) {
					logger.error("Error creating ACS credential", { error });
				}
			},
		}),
		{ name: "user" }
	)
);

export default useUserStore;
